import {
  Badge,
  Button,
  EmptyState,
  ErrorBanner,
  Table,
} from "frontend-kit/components/index.js";
import { submitAchievementClaim } from "./achievement-claim.js";
import { achievementNftConfigured } from "./chain-config.js";
import {
  getClaimProof,
  getPublishStatus,
  listLeaderboard,
  loadBackendConfig,
} from "./gateway-api.js";
import { claimBlockedByPendingChain, resolveStreakEpoch } from "./publish-countdown.js";
import { duskAmountNode } from "./dusk-amount.js";
import { icon } from "./icons.js";
import { LEADERBOARD_PRIVACY_BLURB, maskCommitment } from "./privacy-copy.js";
import { ownCommitmentPrefix } from "./session-identity.js";
import { loadTradingKey } from "./trading-key-store.js";

const STREAK_CLAIM_MIN = 3;
const CUTOVER_MSG =
  "Achievement NFT claim is not live until Phase G cutover - achievementNft must be pinned in contracts.testnet.json.";

function streakEpochFromUrl() {
  const raw = new URLSearchParams(location.search).get("streakEpoch");
  if (!raw) return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

function claimProofUrl(config, commitmentHex, epoch) {
  const hex = commitmentHex.trim().replace(/^0x/i, "");
  const qs = new URLSearchParams({ epoch: String(epoch), type: "1" });
  return `${config.url}/pm/achievements/${encodeURIComponent(hex)}/claim-proof?${qs}`;
}

/**
 * @param {{ log?: Function, wallet?: { state?: { authorized?: boolean, profiles?: unknown[] } } }} opts
 */
export async function renderLeaderboardPage({ log, wallet }) {
  const root = document.createElement("div");
  root.className = "leaderboard-page";

  const header = document.createElement("div");
  header.className = "leaderboard-header";
  const h1 = document.createElement("h1");
  h1.textContent = "Leaderboard";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "portfolio-refresh-btn";
  refreshBtn.title = "Refresh";
  refreshBtn.setAttribute("aria-label", "Refresh leaderboard");
  refreshBtn.append(icon("refresh", { size: 16 }));
  header.append(h1, refreshBtn);
  root.append(header);

  const blurb = document.createElement("p");
  blurb.className = "hint leaderboard-blurb";
  blurb.textContent = LEADERBOARD_PRIVACY_BLURB;
  root.append(blurb);

  const tableHost = document.createElement("div");
  tableHost.className = "leaderboard-table-host";
  root.append(tableHost);

  const statusHost = document.createElement("div");
  statusHost.className = "leaderboard-status-host";
  root.append(statusHost);

  let rows = [];
  let err = null;
  let loading = true;
  let claiming = false;
  const cfg = loadBackendConfig();
  const walletAuthorized = Boolean(
    wallet?.state?.authorized && wallet.state.profiles?.length
  );
  const ownPrefix = ownCommitmentPrefix(
    walletAuthorized,
    loadTradingKey()?.commitment_hex
  );
  const streakEpochUrl = streakEpochFromUrl();
  let streakEpoch = streakEpochUrl;
  let streakPublishStatus = null;
  let streakClaimReady = false;
  const nftLive = achievementNftConfigured();

  async function copyClaimProof(commitmentHex) {
    const proof = await getClaimProof(cfg, commitmentHex, { epoch: streakEpoch, type: 1 });
    await navigator.clipboard.writeText(JSON.stringify(proof, null, 2));
    return proof;
  }

  function paint() {
    statusHost.replaceChildren();
    tableHost.replaceChildren();

    if (err) {
      statusHost.append(ErrorBanner({ message: err }));
    }

    if (loading) {
      tableHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading leaderboard…",
        })
      );
      return;
    }

    if (!rows.length) {
      tableHost.append(
        EmptyState({
          title: "No streak stats yet",
          description:
            "Rankings appear once the indexer has processed finalized markets and derived streak stats.",
        })
      );
      return;
    }

    const scroll = document.createElement("div");
    scroll.className = "portfolio-table-scroll";
    scroll.append(
      Table({
        columns: [
          {
            header: "#",
            render: (row) => String(rows.indexOf(row) + 1),
          },
          {
            header: "Trader",
            render: (row) => {
              const wrap = document.createElement("span");
              wrap.className = "leaderboard-trader mono";
              const fullHex =
                row.commitment_hex ||
                (ownPrefix &&
                row.commitment_prefix.toLowerCase() === ownPrefix &&
                loadTradingKey()?.commitment_hex
                  ? loadTradingKey().commitment_hex
                  : null);
              if (fullHex) {
                const link = document.createElement("a");
                link.href = `#/trader/${fullHex}`;
                link.className = "leaderboard-trader-link";
                link.textContent = maskCommitment(row.commitment_prefix);
                link.title = "Open trader profile";
                wrap.append(link);
              } else {
                wrap.textContent = maskCommitment(row.commitment_prefix);
                wrap.title = maskCommitment(row.commitment_prefix);
              }
              if (ownPrefix && row.commitment_prefix.toLowerCase() === ownPrefix) {
                wrap.append(" ");
                wrap.append(Badge({ variant: "success", label: "You" }));
              }
              return wrap;
            },
          },
          {
            header: "Streak",
            render: (row) => {
              const wrap = document.createElement("span");
              wrap.className = "leaderboard-streak";
              wrap.textContent = String(row.streak);
              if (row.streak >= STREAK_CLAIM_MIN) {
                wrap.append(" ");
                wrap.append(Badge({ variant: "pending", label: "3+ win" }));
              }
              return wrap;
            },
          },
          {
            header: "Correct",
            render: (row) => String(row.total_correct),
          },
          {
            header: "Markets",
            render: (row) => String(row.total_markets),
          },
          {
            header: "Buy vol",
            render: (row) => {
              const wrap = document.createElement("span");
              wrap.append(duskAmountNode(row.buy_volume_lux));
              return wrap;
            },
          },
          {
            header: "Claim",
            render: (row) => {
              if (row.streak < STREAK_CLAIM_MIN) {
                return Object.assign(document.createElement("span"), {
                  className: "hint",
                  textContent: "-",
                });
              }
              const isOwn =
                ownPrefix && row.commitment_prefix.toLowerCase() === ownPrefix;
              if (!isOwn) {
                return Badge({ variant: "neutral", label: "Eligible" });
              }
              const wrap = document.createElement("div");
              wrap.className = "leaderboard-claim-actions";
              if (nftLive && streakClaimReady) {
                wrap.append(
                  Button({
                    label: claiming ? "Claiming…" : "Claim",
                    variant: "primary",
                    disabled: claiming,
                    onClick: async () => {
                      const key = loadTradingKey();
                      if (!key?.commitment_hex) return;
                      claiming = true;
                      statusHost.replaceChildren();
                      paint();
                      try {
                        await submitAchievementClaim({
                          commitmentHex: key.commitment_hex,
                          epoch: streakEpoch,
                          log,
                        });
                        log?.("achievement claim submitted from leaderboard");
                        statusHost.append(
                          Object.assign(document.createElement("p"), {
                            className: "hint leaderboard-claim-ok",
                            textContent: `NFT claim submitted (epoch ${streakEpoch}). Post your bond in Account settings to unlock create.`,
                          })
                        );
                      } catch (e) {
                        statusHost.append(
                          ErrorBanner({ message: e.message || String(e) })
                        );
                      } finally {
                        claiming = false;
                        paint();
                      }
                    },
                  })
                );
              } else if (nftLive && streakPublishStatus?.pending_chain) {
                wrap.append(
                  Object.assign(document.createElement("span"), {
                    className: "hint",
                    textContent: "Snapshot ready - confirming on-chain…",
                  })
                );
              } else if (nftLive) {
                wrap.append(
                  Object.assign(document.createElement("span"), {
                    className: "hint",
                    textContent: "Waiting for publish",
                  })
                );
              }
              wrap.append(
                Button({
                  label: "Copy proof",
                  variant: "secondary",
                  disabled: claiming,
                  onClick: async () => {
                    const key = loadTradingKey();
                    if (!key?.commitment_hex) return;
                    try {
                      await copyClaimProof(key.commitment_hex);
                      statusHost.replaceChildren(
                        Object.assign(document.createElement("p"), {
                          className: "hint leaderboard-claim-ok",
                          textContent: `Claim proof copied (epoch ${streakEpoch}).`,
                        })
                      );
                    } catch (e) {
                      statusHost.replaceChildren(
                        ErrorBanner({ message: e.message || String(e) })
                      );
                    }
                  },
                })
              );
              const linkBtn = document.createElement("button");
              linkBtn.type = "button";
              linkBtn.className = "link-btn leaderboard-proof-link";
              linkBtn.textContent = "API link";
              linkBtn.title = "Copy claim-proof URL";
              linkBtn.addEventListener("click", async () => {
                const key = loadTradingKey();
                if (!key?.commitment_hex) return;
                try {
                  await navigator.clipboard.writeText(
                    claimProofUrl(cfg, key.commitment_hex, streakEpoch)
                  );
                  linkBtn.textContent = "Copied";
                } catch {
                  linkBtn.textContent = "Copy failed";
                }
              });
              const statusLink = document.createElement("a");
              statusLink.href = "#/achievements";
              statusLink.className = "link-btn leaderboard-achievements-link";
              statusLink.textContent = "Status";
              statusLink.title = "View claim status on Achievements";
              wrap.append(linkBtn, statusLink);
              if (!nftLive) {
                wrap.title = CUTOVER_MSG;
              }
              return wrap;
            },
          },
        ],
        rows,
      })
    );
    tableHost.append(scroll);
  }

  async function load() {
    loading = true;
    paint();
    try {
      const [leaderboardResult, statusResult] = await Promise.allSettled([
        listLeaderboard(cfg, 50),
        getPublishStatus(cfg, "streak"),
      ]);
      if (leaderboardResult.status === "fulfilled") {
        rows = leaderboardResult.value;
        err = null;
        log?.(`loaded ${rows.length} leaderboard rows`);
      } else {
        err = leaderboardResult.reason?.message || String(leaderboardResult.reason);
        rows = [];
        log?.(`leaderboard error: ${err}`);
      }
      streakPublishStatus =
        statusResult.status === "fulfilled" ? statusResult.value : null;
      streakEpoch = resolveStreakEpoch(streakPublishStatus, streakEpochUrl);
      const key = loadTradingKey();
      const blocked = claimBlockedByPendingChain(streakPublishStatus, streakEpoch);
      if (key?.commitment_hex && !blocked) {
        try {
          await getClaimProof(cfg, key.commitment_hex, { epoch: streakEpoch, type: 1 });
          streakClaimReady = true;
        } catch {
          streakClaimReady = false;
        }
      } else {
        streakClaimReady = false;
      }
    } catch (e) {
      err = e.message || String(e);
      rows = [];
      streakClaimReady = false;
      log?.(`leaderboard error: ${err}`);
    } finally {
      loading = false;
      paint();
    }
  }

  refreshBtn.addEventListener("click", () => {
    void load();
  });

  await load();
  return root;
}
